import {
  GridList,
  GridListTile,
  GridListTileBar,
  IconButton,
  Paper
} from '@material-ui/core'
import RootRef from '@material-ui/core/RootRef'
import AddIcon from '@material-ui/icons/Add'
import ClearIcon from '@material-ui/icons/Clear'

import * as R from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { FileInterface } from '../../interfaces/file'
import { fileApi } from '../../services'

const StyledGridListContainer = styled.div`
  margin-bottom: 20px;
`

const FormSingleFileDropZone = ({
  fieldName,
  accept = 'image/png, image/jpeg',
  disableForm = false
}: {
  fieldName: string
  accept?: string
  disableForm?: boolean
}) => {
  const { register, unregister, setValue, control } = useFormContext()

  const formFieldValue = useWatch({ control, name: fieldName })

  const [file, setFile]: [
    FileInterface,
    (file?: FileInterface) => void
  ] = useState(formFieldValue) as any

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (R.not(R.isEmpty(acceptedFiles))) {
      const form = new FormData()
      form.append('file', acceptedFiles[0])
      const { data } = await fileApi.uploadFile(form)
      setValue(fieldName, data)
      setFile(data)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: !!file || disableForm,
    multiple: false,
    accept,
    maxSize: 2 * 1024 * 1024,
    onDrop
  })
  const { ref, ...rootProps } = getRootProps()

  const removeFileHandler = () => {
    // TODO: resolve why setValue does not reflect on useWatch for object but works for array in MultiFileUpload
    setValue(fieldName, undefined)
    setFile(undefined)
  }

  useEffect(() => {
    register(fieldName)
    return () => unregister(fieldName)
  }, [register])

  return (
    <StyledGridListContainer>
      <RootRef rootRef={ref}>
        <GridList
          cellHeight={100}
          cols={8}
          style={{
            flexWrap: 'nowrap',
            // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
            transform: 'translateZ(0)'
          }}
        >
          <Paper
            {...rootProps}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0px',
              background: disableForm ? 'grey' : 'white'
            }}
            variant="outlined"
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop here ...</p>
            ) : (
              <AddIcon color="primary" fontSize="large" />
            )}
          </Paper>
          {file && (
            <GridListTile key={file.fileId} style={{ margin: '0px 10px' }}>
              <img src={file.url} alt={file.originalName} />
              <GridListTileBar
                style={{
                  background: 'transparent'
                }}
                titlePosition="top"
                actionPosition="right"
                actionIcon={
                  !disableForm && (
                    <IconButton
                      aria-label="remove file"
                      onClick={() => removeFileHandler()}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }
              />
            </GridListTile>
          )}
        </GridList>
      </RootRef>
    </StyledGridListContainer>
  )
}

export default FormSingleFileDropZone
